import { Intent } from '@blueprintjs/core';
import { query as stardogQuery } from 'stardog';
import { MimeType } from 'vet-bones/constants';
import { VoiceboxMessage } from 'vet-bones/constants/voicebox';

import { DataSourcesActionType } from 'src/actions/data-sources/actionTypes';
import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import { request } from 'src/actions/request/request';
import { FailureResponse, SuccessResponse } from 'src/actions/request/types';
import {
  ActionCreatorAction,
  FluxStandardAction,
  createTypedAction,
} from 'src/actions/types';
import { ProjectPropertyType } from 'src/constants/project';
import { CompetencyQuestion, EvaluationSummary } from 'src/constants/voicebox';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import { Connection } from 'src/store/connections/types';
import {
  PendingFileUpload,
  PendingVirtualGraph,
  Project,
  ProjectFileUpload,
  ProjectModel,
  ProjectVirtualGraph,
  PublishHistory,
} from 'src/store/state/projects';
import { buildGeoDataQuery } from 'src/templates/queries/geoData';
import { buildLabelsAndDescriptionsQuery } from 'src/templates/queries/labelsAndDescriptions';
import { modelClassesQuery } from 'src/templates/queries/modelClasses';
import { modelPropertiesQuery } from 'src/templates/queries/modelProperties';
import { modelRulesQuery } from 'src/templates/queries/modelRules';
import { voiceboxInstructionsQuery } from 'src/templates/queries/voiceboxInstructions';
import { PermissionRequirement } from 'src/utils/hooks/useDesignerPermissions';

export type PublishProjectResponse =
  | {
      success: true;
      action: SuccessResponse<
        any,
        any,
        DatabasesActionType | DataSourcesActionType
      >;
      actionDescription?: never;
      message: string;
    }
  | {
      success: false;
      action: FailureResponse<any, DatabasesActionType | DataSourcesActionType>;
      actionDescription: string;
      message: string;
    };

export const projectsActionCreators = {
  addedProject: ({
    id,
    connectionId,
    modelId,
    name,
    description,
    competencyQuestions,
    dateCreated,
  }: Pick<Project, 'id' | 'name' | 'description'> &
    Partial<Pick<Project, 'dateCreated'>> & {
      connectionId: string | undefined;
      modelId: string;
      competencyQuestions?: CompetencyQuestion[];
    }) =>
    createTypedAction(ProjectsActionType.ADDED_PROJECT, {
      id,
      connectionId,
      modelId,
      competencyQuestions,
      name,
      description,
      dateCreated,
    }),
  addedLocalModelToProject: (
    id: string,
    partialModel: Pick<ProjectModel, 'id' | 'name' | 'namespace'> &
      Partial<ProjectModel>
  ) =>
    createTypedAction(ProjectsActionType.ADDED_LOCAL_MODEL_TO_PROJECT, {
      id,
      partialModel,
    }),
  addedFileUploadsToProject: (
    id: string,
    fileUploads: Array<Omit<ProjectFileUpload, 'dateLastUpdated'>>
  ) =>
    createTypedAction(ProjectsActionType.ADDED_FILE_UPLOADS_TO_PROJECT, {
      id,
      fileUploads,
    }),
  updatedFileUploadForProject: (
    id: string,
    fileUpload: Omit<ProjectFileUpload, 'dateLastUpdated'>
  ) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_FILE_UPLOAD, {
      id,
      fileUpload,
    }),
  addedVirtualGraphsToProject: (
    id: string,
    ...virtualGraphs: Array<Omit<ProjectVirtualGraph, 'dateLastUpdated'>>
  ) =>
    createTypedAction(ProjectsActionType.ADDED_VIRTUAL_GRAPHS_TO_PROJECT, {
      id,
      virtualGraphs,
    }),
  updatedVirtualGraphForProject: (
    id: string,
    virtualGraph: Omit<ProjectVirtualGraph, 'dateLastUpdated'>
  ) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH, {
      id,
      virtualGraph,
    }),
  addedSuggestionsToProject: (id: string) =>
    createTypedAction(ProjectsActionType.ADDED_SUGGESTIONS_TO_PROJECT, {
      id,
    }),
  loadedProjectFromCloud: (project: Project) =>
    createTypedAction(ProjectsActionType.LOADED_PROJECT_FROM_CLOUD, {
      project,
    }),
  importedInvalidProjectFromFile: () =>
    createTypedAction(ProjectsActionType.IMPORTED_INVALID_PROJECT_FROM_FILE),
  importedProjectFromFile: (project: Project) =>
    createTypedAction(ProjectsActionType.IMPORTED_PROJECT_FROM_FILE, {
      project,
    }),
  updatedProjectConnectionId: (id: string, connectionId: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_CONNECTION_ID, {
      id,
      connectionId,
    }),
  updatedProjectVirtualGraphPreviewFailure: (
    message: string,
    isWarning?: boolean
  ) =>
    createTypedAction(
      ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH_PREVIEW_ERROR,
      { message, isWarning }
    ),
  updatedProjectPropertyType: (id: string, propertyType: ProjectPropertyType) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_PROPERTY_TYPE, {
      id,
      propertyType,
    }),
  updatedProjectTargetModelId: (id: string, modelId: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_TARGET_MODEL_ID, {
      id,
      modelId,
    }),
  duplicatedLocalProject: (id: string, copyId: string) =>
    createTypedAction(ProjectsActionType.DUPLICATED_LOCAL_PROJECT, {
      id,
      copyId,
    }),

  /** `filename: null` to mean failure */
  exportedProject: (id: string, filename: string | null) =>
    createTypedAction(ProjectsActionType.EXPORTED_PROJECT, { id, filename }),
  archivedProject: <T>({
    id,
    name,
    restoreAction,
  }: Pick<Project, 'id' | 'name'> & {
    restoreAction: T extends FluxStandardAction<infer U>
      ? FluxStandardAction<U, any>
      : any;
  }) =>
    createTypedAction(ProjectsActionType.ARCHIVED_PROJECT, {
      id,
      name,
      restoreAction,
    }),
  migratedLocalProjectToCloud: (id: string, name: string) =>
    createTypedAction(ProjectsActionType.MIGRATED_LOCAL_PROJECT_TO_CLOUD, {
      id,
      name,
    }),
  migratedCloudProjectToLocal: (id: string, copyId: string, name: string) =>
    createTypedAction(ProjectsActionType.MIGRATED_CLOUD_PROJECT_TO_LOCAL, {
      id,
      copyId,
      name,
    }),
  migratedLocalProjectBackupToCloud: (id: string, name: string) =>
    createTypedAction(
      ProjectsActionType.MIGRATED_LOCAL_PROJECT_BACKUP_TO_CLOUD,
      {
        id,
        name,
      }
    ),
  duplicatedCloudBackupToLocalProject: (id: string, name: string) =>
    createTypedAction(
      ProjectsActionType.DUPLICATED_CLOUD_BACKUP_TO_LOCAL_PROJECT,
      {
        id,
        name,
      }
    ),
  backedUpCloudProjectToLocalProject: (project: Project) =>
    createTypedAction(
      ProjectsActionType.BACKED_UP_CLOUD_PROJECT_TO_LOCAL_PROJECT,
      {
        project,
      }
    ),
  renamedProject: ({ id, name }: Pick<Project, 'id' | 'name'>) =>
    createTypedAction(ProjectsActionType.RENAMED_PROJECT, {
      id,
      name,
    }),
  restoredProject: ({ id, name }: Pick<Project, 'id' | 'name'>) =>
    createTypedAction(ProjectsActionType.RESTORED_LOCAL_PROJECT, {
      id,
      name,
    }),
  restoredCloudProject: ({ id, name }: Pick<Project, 'id' | 'name'>) =>
    createTypedAction(ProjectsActionType.RESTORED_CLOUD_PROJECT, {
      id,
      name,
    }),
  openedMissingProject: ({
    id,
    name,
  }: {
    id: string;
    name: string | undefined;
  }) =>
    createTypedAction(ProjectsActionType.OPENED_MISSING_PROJECT, {
      id,
      name,
    }),
  clearedProjectVoiceboxEvaluation: ({
    id,
    clearSummary,
  }: {
    id: string;
    clearSummary?: boolean;
  }) =>
    createTypedAction(ProjectsActionType.CLEARED_PROJECT_VOICEBOX_EVALUATION, {
      id,
      clearSummary,
    }),
  evaluatedProjectCompetencyQuestions: (id: string, questionIds: string[]) =>
    createTypedAction(
      ProjectsActionType.EVALUATED_PROJECT_COMPETENCY_QUESTIONS,
      {
        id,
        questionIds,
      }
    ),
  evaluatedProjectSummary: (
    id: string,
    evaluationSummary: EvaluationSummary | undefined
  ) =>
    createTypedAction(ProjectsActionType.EVALUATED_PROJECT_SUMMARY, {
      id,
      evaluationSummary,
    }),
  openedProjectCompetencyQuestion: ({
    id,
    question,
  }: {
    id: string;
    question: CompetencyQuestion;
  }) =>
    createTypedAction(ProjectsActionType.OPENED_PROJECT_COMPETENCY_QUESTION, {
      id,
      question,
    }),
  updatedProjectDiagram: (id: string, diagram: DiagramSnapshot) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_DIAGRAM, {
      id,
      diagram,
    }),
  updatedProjectDescription: (id: string, description: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_DESCRIPTION, {
      id,
      description,
    }),
  updatedProjectModel: (
    id: string,
    modelId: string,
    modelUpdate: Partial<ProjectModel>
  ) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_MODEL, {
      id,
      modelId,
      modelUpdate,
    }),
  updatedProjectDataNamespace: (id: string, dataNamespace: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_DATA_NAMESPACE, {
      id,
      dataNamespace,
    }),
  updatedProjectDisableVoiceboxSuggestions: (
    id: string,
    disableVoiceboxSuggestions: boolean
  ) =>
    createTypedAction(
      ProjectsActionType.UPDATED_PROJECT_DISABLE_VOICEBOX_SUGGESTIONS,
      {
        id,
        disableVoiceboxSuggestions,
      }
    ),
  updatedProjectVoiceboxConversation: (id: string, conversationId: string) =>
    createTypedAction(
      ProjectsActionType.UPDATED_PROJECT_VOICEBOX_CONVERSATION,
      {
        id,
        conversationId,
      }
    ),

  acceptedProjectDraftDiagram: (
    id: string,
    diagram: DiagramSnapshot,
    messages: VoiceboxMessage[]
  ) =>
    createTypedAction(ProjectsActionType.ACCEPTED_PROJECT_DRAFT_DIAGRAM, {
      id,
      diagram,
      messages,
    }),
  rejectedProjectDraftDiagram: (
    id: string,
    diagram: DiagramSnapshot,
    messages: VoiceboxMessage[],
    fileUploadIdsToRemove?: string[]
  ) =>
    createTypedAction(ProjectsActionType.REJECTED_PROJECT_DRAFT_DIAGRAM, {
      id,
      diagram,
      messages,
      fileUploadIdsToRemove,
    }),

  addImportedModelsToProjectStarted: ({
    id,
    connectionId,
    databaseId,
    databaseNamespaces,
    modelNamesAndIds,
  }: {
    id: string;
    connectionId: string | null;
    databaseId: string;
    databaseNamespaces: Record<string, string>;
    modelNamesAndIds: Array<Record<'modelId' | 'modelName', string>>;
  }) =>
    createTypedAction(
      ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_STARTED,
      {
        id,
        connectionId,
        databaseId,
        databaseNamespaces,
        modelNamesAndIds,
      }
    ),
  addImportedModelsToProjectFinished: (
    id: string,
    modelNamesAndIds: Array<Record<'modelId' | 'modelName', string>>
  ) =>
    createTypedAction(
      ProjectsActionType.ADD_IMPORTED_MODELS_TO_PROJECT_FINISHED,
      {
        id,
        modelNamesAndIds,
      }
    ),
  addUploadedModelToProjectStarted: ({
    id,
    connectionId,
    databaseId,
    databaseNamespaces,
  }: {
    id: string;
    connectionId: string | null;
    databaseId: string;
    databaseNamespaces: Record<string, string>;
  }) =>
    createTypedAction(
      ProjectsActionType.ADD_UPLOADED_MODEL_TO_PROJECT_STARTED,
      {
        id,
        connectionId,
        databaseId,
        databaseNamespaces,
      }
    ),
  addUploadedModelToProjectFinished: (id: string) =>
    createTypedAction(
      ProjectsActionType.ADD_UPLOADED_MODEL_TO_PROJECT_FINISHED,
      {
        id,
      }
    ),

  generateProjectVoiceboxStoredQueriesAttempt: (id: string) =>
    createTypedAction(
      ProjectsActionType.GENERATE_PROJECT_VOICEBOX_STORED_QUERIES_ATTEMPT,
      { id }
    ),
  generateProjectVoiceboxStoredQueriesFailure: (
    id: string,
    modelName: string,
    error: { message: string; status?: number }
  ) =>
    createTypedAction(
      ProjectsActionType.GENERATE_PROJECT_VOICEBOX_STORED_QUERIES_FAILURE,
      { id, error, modelName }
    ),
  generateProjectVoiceboxStoredQueriesSuccess: (id: string) =>
    createTypedAction(
      ProjectsActionType.GENERATE_PROJECT_VOICEBOX_STORED_QUERIES_SUCCESS,
      { id }
    ),

  publishPermissionDenied: (permissionRequirement: PermissionRequirement) =>
    createTypedAction(ProjectsActionType.PUBLISH_PERMISSION_DENIED, {
      permissionRequirement,
    }),
  publishProjectStarted: ({
    id,
    databaseId,
    publishedDate,
  }: {
    id: string;
    databaseId: string;
    publishedDate: number;
  }) =>
    createTypedAction(ProjectsActionType.PUBLISH_PROJECT_STARTED, {
      id,
      databaseId,
      publishedDate,
    }),
  publishProjectFinished: ({
    id,
    name,
    databaseId,
    publishedDate,
    intent,
    responses,
    publishHistory,
  }: {
    id: string;
    name: string;
    databaseId: string;
    publishedDate: number;
    intent: Intent;
    responses: PublishProjectResponse[];
    publishHistory: PublishHistory;
  }) =>
    createTypedAction(ProjectsActionType.PUBLISH_PROJECT_FINISHED, {
      id,
      name,
      databaseId,
      publishedDate,
      intent,
      responses,
      publishHistory,
    }),
  publishUniqueKeySetsStarted: () =>
    createTypedAction(ProjectsActionType.PUBLISH_UNIQUE_KEY_SETS_STARTED),
  publishUniqueKeySetsFinished: ({
    responses,
  }: {
    responses: PublishProjectResponse[];
  }) =>
    createTypedAction(ProjectsActionType.PUBLISH_UNIQUE_KEY_SETS_FINISHED, {
      responses,
    }),

  uploadedMismatchedHeadersCsv: (filename: string) =>
    createTypedAction(ProjectsActionType.UPLOADED_MISMATCHED_HEADERS_CSV, {
      filename,
    }),
  uploadedPartialCsv: () =>
    createTypedAction(ProjectsActionType.UPLOADED_PARTIAL_CSV),

  refreshProjectModelFailed: () =>
    createTypedAction(ProjectsActionType.REFRESH_PROJECT_MODEL_FAILED),

  refreshProjectModelNoChanges: () =>
    createTypedAction(ProjectsActionType.REFRESH_PROJECT_MODEL_NO_CHANGES),

  addedPendingModelDataToProject: ({
    id,
    modelId,
    classes,
    properties,
  }: {
    id: string;
    modelId: string;
    classes: Record<string, any> | null;
    properties: Record<string, any> | null;
  }) =>
    createTypedAction(ProjectsActionType.ADDED_PENDING_MODEL_DATA_TO_PROJECT, {
      id,
      modelId,
      classes,
      properties,
    }),

  addedPendingFileUploads: (
    projectId: string,
    fileUploads: PendingFileUpload[]
  ) =>
    createTypedAction(ProjectsActionType.ADDED_PENDING_FILE_UPLOADS, {
      projectId,
      fileUploads,
    }),

  clearedPendingFileUploads: (projectId: string) =>
    createTypedAction(ProjectsActionType.CLEARED_PENDING_FILE_UPLOADS, {
      projectId,
    }),

  addedPendingVirtualGraphs: (
    projectId: string,
    virtualGraphs: PendingVirtualGraph[]
  ) =>
    createTypedAction(ProjectsActionType.ADDED_PENDING_VIRTUAL_GRAPHS, {
      projectId,
      virtualGraphs,
    }),

  clearedPendingVirtualGraphs: (projectId: string) =>
    createTypedAction(ProjectsActionType.CLEARED_PENDING_VIRTUAL_GRAPHS, {
      projectId,
    }),

  addedPendingAutoGenerateModel: (projectId: string, resourceIds: string[]) =>
    createTypedAction(ProjectsActionType.ADDED_PENDING_AUTO_GENERATE_MODEL, {
      projectId,
      resourceIds,
    }),

  clearedPendingAutoGenerateModel: (projectId: string) =>
    createTypedAction(ProjectsActionType.CLEARED_PENDING_AUTO_GENERATE_MODEL, {
      projectId,
    }),

  addedPendingAutoGenerateMockData: (projectId: string) =>
    createTypedAction(
      ProjectsActionType.ADDED_PENDING_AUTO_GENERATE_MOCK_DATA,
      { projectId }
    ),

  clearedPendingAutoGenerateMockData: (projectId: string) =>
    createTypedAction(
      ProjectsActionType.CLEARED_PENDING_AUTO_GENERATE_MOCK_DATA,
      { projectId }
    ),
  showedUnsupportedModelPartsWarning: ({
    id,
    modelId,
  }: {
    id: string;
    modelId: string;
  }) =>
    createTypedAction(
      ProjectsActionType.IMPORTED_MODEL_WITH_UNSUPPORTED_PARTS,
      {
        id,
        modelId,
      }
    ),

  receivedCollaborationUpdate: (id: string, project: Partial<Project>) =>
    createTypedAction(ProjectsActionType.RECEIVED_COLLABORATION_UPDATE, {
      id,
      project,
    }),
};

export const projectsStardogRequestDispatchers = {
  requestProjectModelClasses: async ({
    id,
    modelName,
    databaseId,
    namedGraphs,
    shouldNotDispatch,
  }: {
    id: string;
    modelName: string;
    databaseId: string;
    namedGraphs: string[];
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelClassesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_SUCCESS,
      action: { id, modelName, databaseId, namedGraphs },
      shouldNotDispatch,
    }),

  requestProjectModelLabelsAndDescriptions: async ({
    id,
    modelId,
    connection,
    databaseId,
    namedGraphs,
    iris,
    shouldNotDispatch,
  }: {
    id: string;
    modelId: string;
    connection: Connection;
    databaseId: string;
    namedGraphs: string[];
    iris: string[];
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        buildLabelsAndDescriptionsQuery(iris),
        MimeType.JSON_LD,
        { 'default-graph-uri': namedGraphs },
        connection,
      ],
      attempt:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_ATTEMPT,
      failure:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_FAILURE,
      success:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_SUCCESS,
      action: { id, modelId, databaseId, namedGraphs, iris },
      shouldNotDispatch,
    }),

  requestProjectModelGeoData: async ({
    id,
    modelId,
    connection,
    databaseId,
    namedGraphs,
    iris,
    shouldNotDispatch,
  }: {
    id: string;
    modelId: string;
    connection: Connection;
    databaseId: string;
    namedGraphs: string[];
    iris: string[];
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        buildGeoDataQuery(iris),
        MimeType.JSON_LD,
        { 'default-graph-uri': namedGraphs },
        connection,
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_GEO_DATA_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_GEO_DATA_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_GEO_DATA_SUCCESS,
      action: { id, modelId, databaseId, namedGraphs, iris },
      shouldNotDispatch,
    }),

  requestProjectModelProperties: async ({
    id,
    modelName,
    databaseId,
    namedGraphs,
    shouldNotDispatch,
  }: {
    id: string;
    modelName: string;
    databaseId: string;
    namedGraphs: string[];
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelPropertiesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_SUCCESS,
      action: { id, modelName, databaseId, namedGraphs },
      shouldNotDispatch,
    }),
  requestProjectModelRules: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
    connection,
    shouldNotDispatch,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
    connection?: Connection;
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelRulesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_SUCCESS,
      action: { id, modelId, connection },
      shouldNotDispatch,
    }),

  requestProjectModelVoiceboxInstructions: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
    connection,
    shouldNotDispatch,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
    connection?: Connection;
    shouldNotDispatch?: boolean;
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        voiceboxInstructionsQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt:
        ProjectsActionType.REQUEST_PROJECT_MODEL_VOICEBOX_INSTRUCTIONS_ATTEMPT,
      failure:
        ProjectsActionType.REQUEST_PROJECT_MODEL_VOICEBOX_INSTRUCTIONS_FAILURE,
      success:
        ProjectsActionType.REQUEST_PROJECT_MODEL_VOICEBOX_INSTRUCTIONS_SUCCESS,
      action: { id, modelId, connection },
      shouldNotDispatch,
    }),
};

export type ProjectsAction = ActionCreatorAction<
  typeof projectsActionCreators & typeof projectsStardogRequestDispatchers
>;
